import { type yup } from '@framework/validation/initYup.ts';
import { evaluateExpression } from '../schema/expressions/evaluate/evaluateExpression.ts';
import { mapReferenceToPath } from '../schema/expressions/evaluate/mapReferenceToPath.ts';
import { createEvaluationVariables } from '../schema/expressions/evaluate/variables/IEvaluationVariables.ts';
import { collectExpressionReferences } from '../schema/expressions/references/collectExpressionReferences.ts';
import { type ICodeWidget } from '../schema/widgets/ICodeWidget.ts';
import {
  type IParentWidgetScope,
  type IWidgetType,
} from '../schema/widgets/IWidgetType.ts';
import { multiSelectOptionsObjectName } from '../schema/widgets/repository/inputs/multi-select/getMultiSelectOptionsObjectName.ts';
import {
  type IWidgetAdvancedOption,
  type IWidgetOptionValue,
  type Widget,
} from '../schema/widgets/Widget.ts';
import { isParentWidgetScopeReadonly } from './utils/parent-scope/isParentWidgetScopeReadonly.ts';
import { remapBooleanConditionReferencesUpwardInScope } from './utils/remapReferencesUpwardInScope.ts';
import { WidgetConditionManager } from './WidgetConditionManager.ts';

/**
 * Applica le modifiche di "disabilitato" a uno schema durante il cast Yup.
 *
 * Per i campi scalari: transform al default dello schema (o null se assente),
 * così il valore viene "svuotato" ma resta coerente con il tipo.
 *
 * Per gli array: rimuove il campo dal cast. `[]` è un valore valido scelto
 * dall'utente; lo stato disabled deve invece comportarsi come "campo assente".
 * In questo modo, al re-enable, Yup vede `undefined` e può riapplicare il
 * default materializzato di `createRepeatableSchema` (min/showAtLeastOne).
 */
function applyDisabledSchema<S extends yup.AnySchema>(schema: S): S {
  if ((schema as any).type === 'array') {
    return (schema as any).notRequired().strip();
  }
  return (schema as any).transform(
    (_v: unknown, _o: unknown, s: any) => s.getDefault() ?? null,
  );
}

/**
 * Funzione che applica la condizione di abilitazione/disabilitazione
 * allo schema così da poterlo gestire sia nel validatore
 * che in fase di compilazione
 */
export function setEnabledCondition<W extends Widget, S extends yup.AnySchema>(
  schema: S,
  widget: W,
  widgetType: IWidgetType<W>,
  parentScope: IParentWidgetScope = [],
) {
  // In Readonly, non applichiamo la disabilitazione in quanto comunque
  // il campo non è modificabile e il valore corretto è quello consolidato.
  if (isParentWidgetScopeReadonly(parentScope)) return schema.enabled(true);

  const conditionData = WidgetConditionManager.mergeWithScope(
    widget,
    'enabled',
    parentScope,
  );

  if (!conditionData) return schema;
  if (conditionData.condition.type === 'static') {
    return conditionData.condition.value
      ? schema.enabled(true)
      : applyDisabledSchema(schema.enabled(false));
  }

  const { paths, condition, references } = conditionData;

  return schema.parentWhen(paths, (values, schema: any, context: any) => {
    const variables = createEvaluationVariables(references, values);
    const isEnabled = evaluateExpression(condition.expression, { variables });

    return schema.withMutation((s: any) => {
      if (isEnabled) {
        s.enabled(true);
      } else {
        s.enabled(false);
        // Inline applyDisabledSchema dentro withMutation.
        if (s.type === 'array') {
          s.notRequired();
          s.strip();
        } else {
          s.transform(
            (_v: unknown, _o: unknown, resolvedSchema: any) =>
              resolvedSchema.getDefault() ?? null,
          );
        }
      }
      return s;
    });
  });
}

/**
 * Funzione che applica la condizione di abilitazione/disabilitazione
 * allo schema così da poterlo gestire sia nel validatore
 * che in fase di compilazione
 */
export function setOptionEnabledCondition<
  W extends ICodeWidget,
  S extends yup.AnySchema,
>(schema: S, widget: W, option: IWidgetOptionValue | IWidgetAdvancedOption) {
  const condition = remapBooleanConditionReferencesUpwardInScope(
    { code: multiSelectOptionsObjectName(widget.code) },
    option.enabled,
  );

  if (!condition) return schema;
  if (condition.type === 'static') {
    return condition.value
      ? schema.enabled(true)
      : schema.enabled(false).transform(() => schema.getDefault() ?? null);
  }

  const references = collectExpressionReferences(condition.expression);
  const paths = references.map(r => mapReferenceToPath(r));
  return schema.parentWhen(
    paths,
    (values, schema: yup.AnySchema, context: any) => {
      const variables = createEvaluationVariables(references, values);
      const isEnabled = evaluateExpression(condition.expression, { variables });

      return schema.withMutation((s: yup.AnySchema) => {
        if (isEnabled) {
          s.enabled(true);
        } else {
          s.enabled(false);
          s.transform(() => s.getDefault() ?? null);
        }
        return s;
      });
    },
  );
}
